// AdGuard für /sicherheit/dns (docs/dns.md). Summary-Store nach
// docs/data-loading.md:
//
//   Stand     GET /v2-api/adguard -- Kern 30 s, Einrichtung 5 min im
//             Backend gecacht; Abruf alle 30 s, aber nur solange die Seite
//             offen ist (beobachteAdguard, lib/bei-bedarf.ts), letzter
//             Stand aus dem localStorage fürs erste Bild. Nach jedem
//             Handgriff (auch auf einem anderen Gerät) und zum Ende einer
//             Schutz-Pause meldet sich der Dienst auf dem WS-Kanal
//             "adguard" -> sofort neu laden; auch dieses Abo besteht nur mit
//             Beobachter.
//   Query-Log GET /v2-api/adguard/querylog -- nur, solange jemand zusieht
//             (beobachteQueryLog, Seite vorn und Sektion offen): alle 5 s
//             die neueste Seite. Filter und Suche liegen hier, damit ein
//             zweiter Beobachter dasselbe sieht.
//
// Handgriffe (dnsHandgriff) gehen über postJSON: ohne Vault-PIN fragt die
// Oberfläche selbst nach (lib/pin-stufe.ts), danach noch einmal auslösen.

import { untrack } from "svelte";
import { SvelteMap } from "svelte/reactivity";
import { postJSON } from "./api";
import { beiBedarf } from "./bei-bedarf";
import type { AdguardSnapshot, LogFilter, QueryLogEintrag } from "./dns";
import { loadPersisted, persist } from "./persistent-summary.svelte";
import { registerRevalidator } from "./revalidate.svelte";
import { subscribe as wsSubscribe } from "./ws-hub.svelte";

export type { AdguardSnapshot, LogFilter, QueryLogEintrag } from "./dns";

const STORAGE_KEY = "adguard";

const _state = new SvelteMap<string, AdguardSnapshot>();
let _started = false;

async function refresh(): Promise<void> {
  try {
    const r = await fetch("/v2-api/adguard", { credentials: "include" });
    if (!r.ok) return;
    const snap = (await r.json()) as AdguardSnapshot;
    _state.set("current", snap);
    persist(STORAGE_KEY, snap);
  } catch {
    // ignore -- naechster Tick versucht es wieder
  }
}

const _bedarf = beiBedarf({
  hole: refresh,
  takt: 30_000,
  abo: () =>
    wsSubscribe("adguard", () => {
      void refresh();
      if (logBeobachter > 0) void ladeLog();
    }),
});

export function startAdguardSummary(): void {
  if (_started) return;
  _started = true;
  registerRevalidator(() => {
    if (_bedarf.aktiv()) void refresh();
    if (logBeobachter > 0) void ladeLog();
  });
  const cached = loadPersisted<AdguardSnapshot>(STORAGE_KEY);
  if (cached) _state.set("current", cached);
}

// Anmelden, solange die Seite offen ist; der Rückgabewert meldet wieder ab.
// In Svelte: $effect(() => { if (zuSehen()) return beobachteAdguard(); }).
export function beobachteAdguard(): () => void {
  return untrack(() => {
    startAdguardSummary();
    return _bedarf.beobachte();
  });
}

export function stopAdguardSummary(): void {
  _started = false;
  _bedarf.beende();
  _state.clear();
}

export function getAdguardSummary(): AdguardSnapshot | undefined {
  return _state.get("current");
}

// ── Query-Log ─────────────────────────────────────────────────────────

const LOG_LIMIT = 50;
const LOG_MAX = 500;
const LOG_TAKT = 5_000;

export const queryLog = $state<{
  filter: LogFilter;
  suche: string;
  /** neueste zuerst; nach „ältere laden" auch die nachgeladenen */
  eintraege: QueryLogEintrag[];
  /** weiter ab hier (oldest der letzten Seite); leer = nichts Älteres */
  aeltester: string;
  geladen: boolean;
  laedt: boolean;
  fehler: string;
  langsamMs: number;
}>({ filter: "alle", suche: "", eintraege: [], aeltester: "", geladen: false, laedt: false, fehler: "", langsamMs: 100 });

let logBeobachter = 0;
let logTimer: ReturnType<typeof setInterval> | undefined;
let logStand = 0;
let mehrGeladen = false;
let sucheTimer: ReturnType<typeof setTimeout> | undefined;

interface LogAntwort {
  configured: boolean;
  data: QueryLogEintrag[];
  oldest?: string;
  error?: string;
  langsam_ms?: number;
}

function logParameter(aelterAls = ""): string {
  const p = new URLSearchParams({ limit: String(LOG_LIMIT), filter: queryLog.filter });
  const s = queryLog.suche.trim();
  if (s) p.set("suche", s);
  if (aelterAls) p.set("aelter_als", aelterAls);
  return p.toString();
}

const schluessel = (e: QueryLogEintrag): string => `${e.time}|${e.name}|${e.type}|${e.client}`;

/** Die neueste Seite holen. Wer schon Älteres nachgeladen hat, behält es:
 *  die neue Seite kommt davor, was sie überdeckt, fällt weg. */
async function ladeLog(): Promise<void> {
  const nr = ++logStand;
  queryLog.laedt = true;
  try {
    const r = await fetch(`/v2-api/adguard/querylog?${logParameter()}`, { credentials: "include" });
    if (nr !== logStand) return;
    if (!r.ok) {
      queryLog.fehler = `HTTP ${r.status}`;
      return;
    }
    const d = (await r.json()) as LogAntwort;
    if (nr !== logStand) return;
    if (d.langsam_ms) queryLog.langsamMs = d.langsam_ms;
    if (d.error) {
      queryLog.fehler = d.error;
      return;
    }
    queryLog.fehler = "";
    const seite = d.data ?? [];
    if (mehrGeladen && seite.length) {
      const grenze = Date.parse(seite[seite.length - 1].time);
      const rest = queryLog.eintraege.filter((e) => Date.parse(e.time) < grenze);
      queryLog.eintraege = [...seite, ...rest].slice(0, LOG_MAX);
    } else {
      queryLog.eintraege = seite;
      queryLog.aeltester = d.oldest ?? "";
      mehrGeladen = false;
    }
    queryLog.geladen = true;
  } catch (e) {
    if (nr === logStand) queryLog.fehler = e instanceof Error ? e.message : String(e);
  } finally {
    if (nr === logStand) queryLog.laedt = false;
  }
}

/** Ältere Einträge hinten anhängen (AdGuard blättert über older_than). */
export async function ladeAeltere(): Promise<void> {
  if (!queryLog.aeltester || queryLog.laedt) return;
  const nr = ++logStand;
  queryLog.laedt = true;
  try {
    const r = await fetch(`/v2-api/adguard/querylog?${logParameter(queryLog.aeltester)}`, { credentials: "include" });
    if (nr !== logStand || !r.ok) return;
    const d = (await r.json()) as LogAntwort;
    if (nr !== logStand || d.error) return;
    const da = new Set(queryLog.eintraege.map(schluessel));
    const neu = (d.data ?? []).filter((e) => !da.has(schluessel(e)));
    queryLog.eintraege = [...queryLog.eintraege, ...neu].slice(0, LOG_MAX);
    queryLog.aeltester = queryLog.eintraege.length >= LOG_MAX ? "" : (d.oldest ?? "");
    mehrGeladen = true;
  } catch {
    // der nächste Versuch ist ein Klick entfernt
  } finally {
    if (nr === logStand) queryLog.laedt = false;
  }
}

/** Filter oder Suche ändern: von vorn laden (die Suche mit kurzer Pause
 *  beim Tippen). */
export function setzeLogFilter(filter: LogFilter, suche: string): void {
  const neuerFilter = filter !== queryLog.filter;
  if (!neuerFilter && suche === queryLog.suche) return;
  queryLog.filter = filter;
  queryLog.suche = suche;
  mehrGeladen = false;
  if (sucheTimer) clearTimeout(sucheTimer);
  const los = (): void => {
    queryLog.eintraege = [];
    queryLog.aeltester = "";
    queryLog.geladen = false;
    void ladeLog();
  };
  if (neuerFilter) los();
  else sucheTimer = setTimeout(los, 300);
}

/** Zusehen: solange einer zusieht, kommt alle 5 s die neueste Seite. In
 *  Svelte: $effect(() => { if (sichtbar()) return beobachteQueryLog(); }). */
export function beobachteQueryLog(): () => void {
  logBeobachter++;
  if (logBeobachter === 1) {
    void ladeLog();
    logTimer = setInterval(() => void ladeLog(), LOG_TAKT);
  }
  return () => {
    logBeobachter = Math.max(0, logBeobachter - 1);
    if (logBeobachter === 0 && logTimer) {
      clearInterval(logTimer);
      logTimer = undefined;
    }
  };
}

// ── Handgriffe ────────────────────────────────────────────────────────

export type DnsHandgriff = "schutz/pause" | "schutz/an" | "freigeben" | "sperren" | "listen/aktualisieren";

/** Einen Handgriff auslösen; danach gleich den Stand holen (der WS-Rahmen
 *  kommt auch, aber erst nach der Antwort). */
export async function dnsHandgriff<T = unknown>(was: DnsHandgriff, body: Record<string, unknown> = {}): Promise<T> {
  const r = await postJSON<T>(`/v2-api/adguard/${was}`, body);
  void refresh();
  return r;
}
